/* eslint-disable @typescript-eslint/no-non-null-assertion */
import _ from "underscore";
import analytics from "web/script/analytics/analytics";
import eventEmitter from "web/script/mixins/event-emitter";
import globals from "web/script/modules/globals";
import browser from "./browser";
import diff, { DiffResult } from "./diff";
import url, { URLQuery } from "./url";
import uuid from "./uuid";

export interface BrowserHistoryEvent {
    queryChanges: DiffResult;
    path: string;
    oldPath: string | null;
    oldQuery: URLSearchParams | null;
    query: URLSearchParams;
    state: any;
    // Browser events come in as `popstate` while the other values come from our history methods
    // TODO: Make all of these lowercase to avoid accidents
    eventType: "popstate" | "popState" | "pushState" | "replaceState";
}
/**
 * A browser history object which wraps the History API.
 */
export class BrowserHistory {
    _window: Window | null = null;
    _currentQuery: URLSearchParams | null = null;
    _currentPath: string | null = null;

    /**
     * Keeps a history of scroll positions when a new state is pushed. This is restored
     * when the state has been popped (i.e. gone back).
     */
    _scrollPositionHistoryQueue: number[] | null = null;

    constructor() {
        this._onLocationChange = this._onLocationChange.bind(this);
        this._onHashChange = this._onHashChange.bind(this);
    }
    on = eventEmitter.on;
    off = eventEmitter.off;
    trigger = eventEmitter.trigger;

    /**
     * Starts listening for history changes, and fires any history being listened to.
     */
    start(win: Window): this {
        if (this._window) {
            throw new Error("History has already been started.");
        }

        this._window = win || globals.window;
        this._scrollPositionHistoryQueue = [];

        this._window.addEventListener("popstate", this._onLocationChange);
        this._window.addEventListener("hashchange", this._onHashChange);
        return this;
    }

    /**
     * Unbinds all event listeners and resets all state.
     */
    stop(): this {
        if (!this._window) {
            return this;
        }

        this._scrollPositionHistoryQueue = null;
        this._currentPath = null;
        this._currentQuery = null;
        this._window.removeEventListener("popstate", this._onLocationChange);
        this._window.removeEventListener("hashchange", this._onHashChange);
        this._window = null;
        return this;
    }

    /**
     * Replaces a new state into history. The argument order of this method is different
     * from the browser API to reflect the most common use cases.
     */
    replaceState(path: string, query: URLQuery, title?: string, data?: object): this {
        return this._updateHistory("replaceState", path, query, undefined, title, data);
    }

    /**
     * Pushes the current state in the history. The argument order of this method is
     * different from the browser API to reflect the most common use cases.
     */
    pushState(
        path: string,
        query: URLQuery,
        title?: string,
        data?: object,
        bypassRouting?: boolean,
        resetScroll?: boolean
    ): this {
        return this._updateHistory(
            "pushState",
            path,
            query,
            undefined,
            title,
            data,
            bypassRouting,
            resetScroll
        );
    }

    /**
     * Causes the history to go back one step. This may cause a new page load or a
     * 'popstate' event depending on how the current state was triggered.
     * @returns {BrowserHistory}
     */
    back(): this {
        this._window!.history.back();
        return this;
    }

    /**
     * Returns the current pathname.
     * @returns {string}
     */
    getCurrentPath(): string {
        return this._window!.location.pathname;
    }

    /**
     * Returns an object containing the current query parameters.
     */
    getCurrentQueryParams(): URLSearchParams {
        return url.toURLSearchParams(this._window!.location.search);
    }

    /**
     * Updates the query string of the url without changing the path.
     * @param query The new query parameters to add or update
     * @param replace If replaceState should be used instead of pushState
     */
    updateQuery(query: URLSearchParams, replace?: boolean): this;
    /**
     * Updates part of the query string of the url without changing the path.
     * @param key The key of the query string to set
     * @param value The new value to set it to
     * @param replace If replaceState should be used instead of pushState
     */
    updateQuery(key: string, value: string | null, replace?: boolean): this;
    updateQuery(
        keyOrQuery: URLSearchParams | string,
        valueOrReplace: string | boolean | null | undefined,
        replace = false
    ): this {
        let params: URLSearchParams;
        if (_.isString(keyOrQuery)) {
            params = this.getCurrentQueryParams();
            if (valueOrReplace) {
                params.set(keyOrQuery, valueOrReplace.toString());
            } else {
                params.delete(keyOrQuery);
            }
        } else {
            params = keyOrQuery;
            replace = !!valueOrReplace;
        }

        return this._updateHistory(
            replace ? "replaceState" : "pushState",
            this.getCurrentPath(),
            url.toURLSearchParams(params),
            this._window!.location.hash
        );
    }

    /**
     * Convenience method for subscribing to a particular query parameter.
     * The main different with this method in comparison to 'on' is that the
     * callback will be called straight away if the value of the parameter is
     * truth-y at the time of call.
     * @param {string} key The key to subscribe to.
     * @param {function} callback The callback to call when it changes.
     * @returns {function} A function that if called unbinds the handler.
     */
    bindQuery(
        key: string,
        callback: (
            action: "added" | "deleted" | "changed",
            values: string[] | undefined,
            oldValues: string[] | undefined
        ) => void
    ): () => void {
        let value: string[];

        if (this._window) {
            value = this.getCurrentQueryParams().getAll(key);

            // if value is already present and the history module has
            // already been started then call the callback.
            if (value.length) {
                callback("added", value, undefined);
            }
        }

        function handler(event: any, data: { queryChanges: DiffResult[] }): void {
            let record = _.findWhere(data.queryChanges, { key: key });

            if (!record || _.isEqual(record.value, value)) {
                return;
            }

            value = record.value;
            callback(record.type, record.value, record.oldValue);
        }

        this.on("change", handler);

        // return a function that can remove this binding
        return (): void => {
            this.off("change", handler);
        };
    }

    /**
     * Updates the history state using the given method.
     */
    private _updateHistory(
        method: "pushState" | "popState" | "replaceState",
        path: string,
        query?: URLQuery,
        hash?: string,
        title?: string,
        state?: any,
        bypassRouting = false,
        resetScroll = false
    ): this {
        if (_.isEmpty(path)) {
            return this;
        }

        let parsed = url.parse(path);

        // extend any query params present on `path` with ones provided in `query`
        let mergedParams = _.extend(
            parsed.searchParams.toJSON(),
            url.toURLSearchParams(query).toJSON()
        );
        parsed.searchParams = url.toURLSearchParams(mergedParams);
        parsed.hash = hash || "";

        let changes = this._getChanges(this._currentQuery!, parsed.searchParams);

        if (!changes.length && parsed.path === this._currentPath) {
            return this;
        }

        if (!browser.supportsHistory) {
            this._window!.location.href = url.unparse(parsed);
            return this;
        }

        // fix scroll position bugs in certain browsers, only for pushState
        if (method === "pushState") {
            // fix scroll position when popping to a previous state on the feeds pagination
            this._scrollPositionHistoryQueue!.push(resetScroll ? 0 : this._window!.pageYOffset);
        }

        state = state || {};
        if (!state.uuid) {
            state.uuid = uuid.uuid4();
        }

        if (!state.pageViewId) {
            // Set the pageViewID associated with this page state
            analytics.generatePageViewId();
            state.pageViewId = analytics.getPageViewId();
        }

        this._window!.history[method](state, title, url.unparse(parsed));
        // needed so that the feeds pagination does not reset when loading new pages
        if (!bypassRouting) {
            this._onLocationChange({
                state: state,
                type: method.toLowerCase(),
            });
        }
        return this;
    }

    /**
     *
     * @param [event] If event is passed then it is being called from 'popstate' event,
     *                otherwise it is being called manually following a pushState or
     *                replaceState.
     */
    private _onLocationChange(event: { type: string; state: any }): void {
        let oldQuery = this._currentQuery!;
        let oldPath = this._currentPath!;
        let query = this.getCurrentQueryParams();
        let path = this._window!.location.pathname;
        let queryChanges = this._getChanges(oldQuery, query);

        this._currentQuery = query;
        this._currentPath = path;

        this.trigger("change", {
            queryChanges: queryChanges,
            path: path,
            oldPath: oldPath,
            oldQuery: oldQuery,
            query: query,
            state: event.state,
            eventType: event.type,
        });

        // if event, then it's a popstate.
        if (event && event.type === "popstate") {
            // Restore the pageViewId
            const state = this._window!.history.state;
            if (state) {
                analytics.setPageViewId(state.pageViewId);
            }
            // Restore the scroll position for browsers that are
            // buggy in restoring it on changing the URL on back.
            let scrollPosition = this._scrollPositionHistoryQueue!.pop();
            if (_.isNumber(scrollPosition)) {
                _.defer((scrollPosition) => {
                    this._window!.scrollTo(0, scrollPosition);
                }, scrollPosition);
            }
        }
    }

    /**
     * Handler for the hashchange event.
     * @param event
     */
    private _onHashChange(event): void {
        this.trigger("hashchange", event);
    }

    /**
     * Calculates the changes between two sets of query params (ie. added, changed, removed.)
     */
    _getChanges(queryA: URLSearchParams, queryB: URLSearchParams): DiffResult[] {
        let queryAJSON = url.toURLSearchParams(queryA).toJSON();
        let queryBJSON = url.toURLSearchParams(queryB).toJSON();

        return diff(queryAJSON, queryBJSON);
    }
}

// create the instance exported by this module
export default new BrowserHistory();
